import React, { useState, useEffect, useContext } from 'react';
import './AllForVote.scss';
import { inject, observer } from 'mobx-react';
import RootStore from '../../store/RootStore';
import { IVote, IVoteOption, IUserVote } from '../../store/VoteStore';
import { IComponentVote } from '../../component';
import intl from 'react-intl-universal';
import { getPageSameType, fixedLength } from '../../utils/util';
import { ThemeContext } from '../../store/ThemeProvider';
import moment from 'moment';
import Search from './Search';
import { style } from '../../utils/image';
import chroma from 'chroma-js';
import { getPendantColor } from '../../utils/color';
import useWaterfall from 'components/hooks/useWaterfall';
import SimpleBigPic from 'components/BigPic/SimpleBigPic';

export interface IVoteProps {
  data: IVote;
  rootStore?: RootStore;
  show_search: boolean;
  show_count: boolean;
  show_result: boolean;
  static_order?: boolean;
  show_bigpic?: boolean;
  showtype?: string;
  button_text?: string;
  mode: string;
  backgroundColor?: string;
  onVote: (target: string) => void;
}

const voteTimeFormat = (time: string, mainColor?: string) => {
  let timestamp = moment(time).valueOf() - Date.now();
  let duration = moment.duration(timestamp, 'ms');
  let array = [
    <React.Fragment key='hour'>
      <span className="timer-value" style={{ color: mainColor }}>{fixedLength(duration.get('hour'))}</span><span className="timer-unit">{intl.get('Hour')}</span>
    </React.Fragment>,
    <React.Fragment key='minute'>
      <span className="timer-value" style={{ color: mainColor }}>{fixedLength(duration.get('minute'))}</span><span className="timer-unit">{intl.get('Minute')}</span>
    </React.Fragment>,
    <React.Fragment key='second'>
      <span className="timer-value" style={{ color: mainColor }}>{fixedLength(duration.get('second'))}</span><span className="timer-unit">{intl.get('Second')}</span>
    </React.Fragment>
  ];
  if (timestamp > 86400 * 1000) {
    // 大于一天
    array.unshift(<React.Fragment key='day'>
      <span className="timer-value" style={{ color: mainColor }}>{Math.floor(timestamp / (86400 * 1000))}</span><span className="timer-unit">{intl.get('Day')}</span>
    </React.Fragment>);
  }

  return array;
};

const timelineFormat = (
  begin_at: string,
  end_at: string,
  status: string | number,
  text?: string,
  mainColor?: string,
) => {
  if (moment().isBefore(moment(begin_at))) {
    return <React.Fragment>
      <span>{intl.get('VoteWait')}</span>
      {voteTimeFormat(begin_at, mainColor)}
    </React.Fragment>;
  } else if (moment().isAfter(moment(end_at))) {
    return text ? `${text}已结束` : intl.get('VoteHadEnd');
  } else if (
    moment().isBefore(moment(end_at)) &&
    moment().isAfter(moment(begin_at))
  ) {
    return <React.Fragment>
      <span>{intl.get('VoteEnd')}</span>
      {voteTimeFormat(end_at, mainColor)}
    </React.Fragment>;
  }
};

const Header = (props: {
  title: string;
  show_count: boolean;
  begin_at: string;
  end_at: string;
  total?: number;
  status: string | number;
  vote_limit?: string | number;
  user_voted?: number;
  bool_loop?: number;
  button_text?: string;
}) => {
  const { componentColor, mainColor, h1Color, h2Color } = useContext(
    ThemeContext
  );

  return (
    <div className="vote-header">
      <div className="vote-header-title" style={{ color: h1Color }}>
        {props.title}
      </div>
      <div className="vote-header-info" style={{ color: h1Color }}>
        {props.show_count ? `${props.total}${props.button_text ? `人参与${props.button_text}` : intl.get('VoteJoin')}，` : ''}
        每人{!!props.bool_loop ? '每天' : ''}可{props.button_text || '投票'}{Number(props.vote_limit || 0)}次
      </div>
      <div className="vote-right-info" style={{ color: h2Color }}>
        {timelineFormat(props.begin_at, props.end_at, props.status, props.button_text, mainColor)}
      </div>
    </div>
  );
};

const Vote = (props: IVoteProps) => {
  const { componentColor, mainColor, h1Color, h2Color } = useContext(
    ThemeContext
  );
  const {
    show_search,
    show_result,
    show_count,
    static_order,
    button_text,
    show_bigpic,
    showtype,
    data,
    onVote,
    rootStore
  } = props;
  const { id, type, content, status, begin_at, end_at } = data;

  const shouldShowSearch = () => {
    if (show_search) {
      if (content && content instanceof Array) {
        if (content.length > 8) {
          return true;
        }
      }
    }

    return false;
  };

  const isStart = () => {
    if (Number(status) == 1) {
      if (moment().isAfter(moment(begin_at)) && moment().isBefore(end_at)) {
        return true;
      }
    }

    return false;
  };

  const canDoVote = () => {
    if (Number(data.bool_loop)) {
      let userVoted = rootStore!.voteStore.getUserVoted(data.id);
      if (userVoted) {
        // @ts-ignore
        return isStart() && Number(userVoted.num) > 0; // 还要判断一下，当日投票数小于可投票数
      } else {
        return isStart();
      }
    } else {
      let userVoted = rootStore!.voteStore.getUserVoted(data.id);
      let total = 0;
      if (userVoted && userVoted instanceof Array) {
        userVoted.forEach((it: IUserVote) => {
          total += Number(it.num);
        });
      }
      return isStart() && total < Number(data.has_voted);
    }
  };

  const getUserVotedCount = () => {
    if (data && data.id) {
      let userVoted = rootStore!.voteStore.getUserVoted(data.id);

      if (data && userVoted && Number(data.bool_loop)) {
        // @ts-ignore 
        return Math.max(0, Number(userVoted.num));
      }

      if (data) {
        if (userVoted && userVoted instanceof Array) {
          let total = 0;
          userVoted.forEach((it: IUserVote) => {
            total += Number(it.num);
          });
          return Math.max(0, Number(data.has_voted) - total);
        } else {
          return Number(data.has_voted);
        }
      }
    }

    return 0;
  }

  const canVote = canDoVote();

  const [filter, setFilter] = useState('');
  const [backgroundColor, foreColor] = getPendantColor(mainColor);

  // 大图相关的内容
  const [bigIndex, setBigIndex] = useState(-1 as number);
  const [bigList, setBigList] = useState([] as any[]);
  const [bigIsVoted, setBigIsVoted] = useState(false);

  useEffect(() => {
    if (bigIndex >= 0) {
      const item = bigList[bigIndex];
      const isVoted = rootStore!.voteStore.isVoteByOption(data.id, item.value) && Number(data.has_voted) == 1;
      setBigIsVoted(isVoted);
    } else {
      setBigIsVoted(false);
    }
  }, [bigIndex]);

  const reformatOptionList = () => {
    let result = content.filter((it: IVoteOption) => {
      if (filter) {
        return it && it.name && it.name.indexOf(filter) > -1;
      }
      return true;
    });

    if (result && result.length) {
      let sortResult = result.map((it: IVoteOption) => {
        let voteCount = rootStore!.voteStore.getOptionCount(
          data.id,
          it.value
        );
        it.num = voteCount.count;
        it.vote_percent = voteCount.percent;

        return it;
      });
      if (!static_order) {
        sortResult.sort((a: IVoteOption, b: IVoteOption) => {
          return Number(a.num) > Number(b.num) ? -1 : 1;
        });
      }

      return sortResult;
    }

    return [];
  }

  const reformatBigList = () => {
    const sortResult = reformatOptionList();

    if (sortResult && sortResult.length) {
      const imageList = sortResult.map((it: IVoteOption, index: number) => {
        return {
          id: index,
          value: it.value,
          name: it.name,
          num: it.num,
          hash: it.image,
        };
      }).filter(Boolean);

      setBigList(imageList);
      return imageList;
    } else {
      setBigList([]);
      return [];
    }
  };

  const onImageClick = (it: IVoteOption) => {
    if (show_bigpic) {
      let imageList = reformatBigList();
      let targetIndex = imageList.findIndex((one: any) => {
        if (one && (one.value == it.value)) {
          return true;
        }
        return false;
      });
      setBigList(imageList);
      setBigIndex(targetIndex);
    }
  };

  const renderOption = (it: IVoteOption, isVoted: boolean, isWaterfall?: boolean) => {
    return (
      <div
        className="vote-option"
        key={it.value}
        style={{
          borderColor: chroma(h1Color)
            .alpha(0.1)
            .css()
        }}
      >
        {String(type) == '2' ? (
          <img src={style(it.image, isWaterfall ? 'wx_375_240' : 'avatar_150')} className={'thumb'} onClick={() => {
            onImageClick(it);
          }} />
        ) : null}
        <div className="detail">
          <div className="name" style={{ color: h1Color }}>
            {it.name}
          </div>
          {show_result ? (
            <div className="voted">
              <div className="number" style={{ color: h1Color }}>
                {it.num}票
              </div>
              <div className="progress">
                <div
                  className="progress-wrapper"
                  style={{
                    backgroundColor: chroma(h1Color)
                      .alpha(0.1)
                      .css()
                  }}
                >
                  <div
                    className="percent"
                    style={{
                      width: `${it.vote_percent}%`,
                      backgroundColor: mainColor
                    }}
                  />
                </div>
                <div className="ptext" style={{ color: h2Color }}>
                  {it.vote_percent}%
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {!isVoted ? (
          <div
            className={'btn'}
            style={
              canVote
                ? { background: mainColor, color: foreColor }
                : {
                  background: chroma(h1Color)
                    .alpha(0.3)
                    .css(),
                  color: foreColor
                }
            }
            onClick={
              canVote ? (e: any) => onVote(it.value) : (e: any) => { }
            }
          >
            {button_text ? button_text : intl.get('Vote')}
          </div>
        ) : (
          <div
            className="btn"
            style={{
              background: foreColor,
              color: mainColor,
              border: `1px solid ${mainColor}`
            }}
          >
            {button_text ? `已${button_text}` : intl.get('Voted')}
          </div>
        )}
      </div>
    )
  };

  const getOptions = () => {
    if (content && content instanceof Array) {
      const sortResult = reformatOptionList();
      if (sortResult.length) {
        if (showtype == 'waterflow') {
          const [column1, column2] = useWaterfall(sortResult, 375, 240);
          return <React.Fragment>
            <div className="column-w column-1">
              {column1.map((it: any) => {
                let isVoted = rootStore!.voteStore.isVoteByOption(data.id, it.value) && Number(data.has_voted) == 1;
                return renderOption(it as IVoteOption, isVoted, true);
              })}
            </div>
            <div className="column-w column-2">
              {column2.map((it: any) => {
                let isVoted = rootStore!.voteStore.isVoteByOption(data.id, it.value) && Number(data.has_voted) == 1;
                return renderOption(it as IVoteOption, isVoted, true);
              })}
            </div>
          </React.Fragment>;
        } else {
          return sortResult.map((it: IVoteOption) => {
            let isVoted = rootStore!.voteStore.isVoteByOption(data.id, it.value) && Number(data.has_voted) == 1;
            return renderOption(it, isVoted);
          });
        }
      } else {
        return (
          <div className="vote-option-empty" style={{ color: h2Color }}>
            <img src={require('../../assets/vote_empty.png')} />
            {intl.get('VoteEmpty')}
          </div>
        );
      }
    }

    return [];
  };

  return (
    <div className="vote">
      <Header
        title={data.question_title}
        show_count={show_count}
        begin_at={data.begin_at}
        end_at={data.end_at}
        total={data.user_voted}
        status={data.status}
        vote_limit={data.has_voted}
        bool_loop={data.bool_loop}
        button_text={button_text}
      // user_voted={getUserVotedCount()}
      />
      {shouldShowSearch() ? (
        <Search
          search={(e: string) => {
            setFilter(e);
          }}
          placeholder={intl.get('InputTheKeyword')}
          mainColor={mainColor}
          h1Color={h1Color}
          h2Color={h2Color}
        />
      ) : null}
      <div
        className={'vote-list ' + (showtype == 'waterflow' ? 'waterflow' : '')}
        style={{
          borderColor: chroma(h1Color)
            .alpha(0.1)
            .css()
        }}
      >
        {getOptions()}
      </div>

      <SimpleBigPic
        pageType="VoteList"
        index={bigIndex}
        list={bigList}
        setIndex={setBigIndex}
        bigPicInfoRender={<React.Fragment>
          <span
            className="big-pic-info"
            style={{
              opacity: canVote ? 1 : 0.75
            }}
            onClick={canVote ? () => {
              // 是否可投票
              if (!bigIsVoted) {
                let item = bigList[bigIndex];
                console.log(item);
                onVote(item.value);
              }
            } : (e: any) => { }}
          >
            {bigIsVoted ? (
              button_text ? `已${button_text}` : intl.get('Voted')
            ) : (
              button_text ? button_text : intl.get('Vote')
            )}
          </span>
          {bigList && bigList[bigIndex] ? <React.Fragment>
            <span className="big-pic-vote-name" style={{ position: `absolute`, bottom: `110%`, left: `10px`, fontSize: `14px`, fontWeight: `bold` }}>
              <p>{bigList[bigIndex].name}</p>
              <p>{bigList[bigIndex].num}票</p>
            </span>
          </React.Fragment> : null}
        </React.Fragment>}
      />
    </div>
  );
};

export default inject('rootStore')(observer(Vote));